import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Result } from "antd";
import {
   DownOutlined
} from "@ant-design/icons";
import { t } from "i18next";
import {
   Row,
   Col,
   Dropdown,
   Menu,
   Table,
   Breadcrumb,
   Button,
   Input,
   SearchOutlined,
   PlusOutlined,
   Form
} from "common/elements";
import { Util } from "common/util";

export default function withDataTable({
   title = "",
   addNewLink = "",
   updateLink = "",
   pathName = "/",
   columns = [],
   defaultPageSize = 20,
   permission = "PIP",
   getData = () => { },
   showButtonSearch = true,
   showPagination = true,
   tableScrollY = null,
   FormInsertOrUpdate = () => { },
   handleAddNew = (modalRef) => { },
   confirmSoftDelete = (id) => { },
   componentWillMount = () => { },
   handleSearch = (e) => { }
}) {
   return () => {
      const [datas, setDatas] = React.useState([]);
      const [loading, setLoading] = React.useState(true);
      const [form] = Form.useForm();
      const params = new URLSearchParams(document.location.search);
      const navigate = useNavigate();
      let modalRef = React.createRef();
      let noPermission = false;
      let timer = null;

      const fetchList = () => {
         setLoading(true);
         getData()
            .then((response) => {
               setDatas(response);
            })
            .catch(error => {
               if (error.response.status === 403) {
                  localStorage.removeItem("ACCESS_TOKEN");
                  navigate("/signin");
               }
            })
            .finally(() => setLoading(false));
      };

      const onSearch = (e) => {
         clearTimeout(timer);
         handleSearch(e);
         timer = setTimeout(() => {
            fetchList();
         }, 1000);
      };

      const onAddNew = () => {
         if (addNewLink) {
            navigate(addNewLink);
         } else {
            handleAddNew(modalRef);
         }
      };

      const onDelete = (id) => {
         Util.getInstance()
            .sweetAlertConfirm(t("text_confrim"), "You won't be able to revert this!", ["Cancel", "Yes, delete it!"])
            .then((willDelete) => {
               if (willDelete) {
                  confirmSoftDelete(id)
                  .then(() => {
                     fetchList();
                  });
               }
            });
      };

      const onTableChange = (newPagination, filters, sorter) => {
         fetchList({
            sortField: sorter.field,
            sortOrder: sorter.order,
            pagination: newPagination,
            ...filters,
         });
      };

      const onChangePagination = (page, pageSize) => {
         const params = new URLSearchParams(document.location.search);
         params.set("current", page);
         params.set("size", pageSize);
         Util.getInstance().pushParamsToURL(pathName, params.toString());
         fetchList();
      };

      const onShowSizeChange = (current, size) => {
         const params = new URLSearchParams(document.location.search);
         params.set("current", current);
         params.set("size", size);
         Util.getInstance().pushParamsToURL(pathName, params.toString());
         fetchList();
      };

      React.useMemo(() => {
         componentWillMount();
      }, []);

      React.useEffect(() => {
         fetchList();
         // eslint-disable-next-line
      }, []);
      const pagination = {
         current: params.has("current") ? parseInt(params.get("current")) : 1,
         defaultPageSize,
         pageSizeOptions: ["20", "30", "50", "100"],
         total: datas.pagination?.total,
         showSizeChanger: true,
         locale: { items_per_page: "" },
         onChange: onChangePagination,
         onShowSizeChange,
         showTotal: (total, range) => `${t("text_display")} ${range[0]}-${range[1]} ${t("text_out_of")} ${total}`,
      };
      if (permission && Util.getInstance().hasPermission("view", permission)) {
         noPermission = false;
      }

      if (noPermission) {
         return (
            <Result
               status="403"
               title="403"
               subTitle={t("text_sorry_you_are_not_authorized_to_access_this_page")}
            />
         );
      }

      return (
         <>
            <Row style={styles.breadCream}>
               <Col span={24}>
                  <Breadcrumb>
                     <Breadcrumb.Item
                        onClick={() => navigate("/")}
                        style={{ cursor: "pointer" }}
                     >
                        {t("text_dashboard")}
                     </Breadcrumb.Item>
                     <Breadcrumb.Item style={{ color: "#1d8747" }}>
                        {title}
                     </Breadcrumb.Item>
                  </Breadcrumb>
               </Col>
            </Row>

            <Row style={{ padding: 15 }}>
               <Col span={8}>
                  <Title title={title} />
               </Col>
               <Col span={16} style={{ textAlign: "right", bottom: "5px" }}>
                  {
                     showButtonSearch && (
                        <Input
                           placeholder={t("text_search")}
                           defaultValue={params.get("search")}
                           prefix={<SearchOutlined />}
                           style={styles.generalSearch}
                           allowClear={true}
                           onChange={onSearch}
                        />
                     )
                  }
                  
                  {Util.getInstance().hasPermission("create", permission) && (
                     <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={onAddNew}
                        style={{ marginLeft: 10 }}
                     >
                        Add New
                     </Button>
                  )}
               </Col>
            </Row>

            <div className="data-table" style={{ flex: 1, display: "block" }}>
               <Table
                  dataSource={datas.data}
                  pagination={showPagination ? pagination: false}
                  onChange={onTableChange}
                  rowKey={(record) => `${record.id}`}
                  scroll={datas.pagination && datas.pagination.count > 10 ? { y: tableScrollY ? tableScrollY : window.innerHeight - 310 } : {}}
                  columns={[
                     ...columns,
                     {
                        title: t("text_action"),
                        dataIndex: "id",
                        key: "id",
                        render: (id, record) => {
                           return <Dropdown overlay={
                              <Menu>
                                 <Menu.Item key={0} onClick={() => onDelete(id)}>
                                    Delete
                                 </Menu.Item>
                                 <Menu.Item key={1}>
                                    <Link to={`${updateLink}/${id}`}>Edit</Link>
                                 </Menu.Item>
                              </Menu>
                              }>
                              <Button
                                 style={{ marginLeft: 10 }}
                              >
                                 Options
                                 <DownOutlined style={{fontSize: 14}} />
                              </Button>
                           </Dropdown>;
                        },
                     },
                  ]}
                  loading={loading}
               />
            </div>

            <FormInsertOrUpdate
               ref={(f) => (modalRef = f)}
               onSuccess={fetchList}
               form={form}
               navigate={navigate}
            />
         </>
      );
   };
}

function Title({ title }) {
   return <h4 style={{ marginBottom: 0, fontWeight: 600 }}>{title}</h4>;
}

const styles = {
   generalSearch: {
      width: 200,
   },
   headerFilter: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
   },
   breadCream: {
      paddingBottom: 15,
      backgroundColor: "#EFF3F6",
   },
   style: {
      background: "#0092ff",
      padding: "8px 0",
   },
};
